import React, { useState } from 'react';
import { Brain, Mic, BarChart3, Cpu, BookOpen, ShoppingBag, Shield, Settings, Server, Database, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import DashboardSection from './components/DashboardSection';
import GpuSection from './components/GpuSection';
import VoiceWizard from './components/VoiceWizard';
import VoiceSession from './components/VoiceSession';
import ModelSection from './components/ModelSection';
import TrainingSection from './components/TrainingSection';
import MarketplaceSection from './components/MarketplaceSection';
import AdminSection from './components/AdminSection';
import SettingsSection from './components/SettingsSection';
import ClusterSection from './components/ClusterSection';
import DataSection from './components/DataSection';
import ComputeSection from './components/ComputeSection';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'gpu', label: 'GPU', icon: Cpu },
  {
    id: 'voice',
    label: 'Voice',
    icon: Mic,
    subItems: [
      { id: 'voice_wizard', label: 'Wizard' },
      { id: 'voice_session', label: 'Session' },
    ],
  },
  { id: 'model', label: 'Model', icon: Brain },
  { id: 'training', label: 'Training', icon: BookOpen },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
  { id: 'compute', label: 'Compute', icon: Zap },
  { id: 'cluster', label: 'Cluster', icon: Server },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'admin', label: 'Admin', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function App() {
  const [view, setView] = useState('dashboard');
  const [subView, setSubView] = useState('voice_wizard');
  const [voiceExpanded, setVoiceExpanded] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [wizardConfig, setWizardConfig] = useState(null);

  const handleStartSession = (config) => {
    setWizardConfig(config);
    setSessionActive(true);
    setSubView('voice_session');
  };

  const handleStopSession = () => {
    setSessionActive(false);
    setWizardConfig(null);
    setSubView('voice_wizard');
  };

  const handleNavClick = (id) => {
    if (id === 'voice') {
      setVoiceExpanded(!voiceExpanded);
      if (!voiceExpanded) {
        setView('voice');
      }
    } else {
      setView(id);
      setVoiceExpanded(false);
    }
  };

  const handleVoiceSubClick = (subId) => {
    setView('voice');
    setSubView(subId);
    setVoiceExpanded(true);
  };

  const isActive = (id) => {
    if (view === id) return true;
    if (id === 'voice' && view === 'voice') return true;
    return false;
  };

  const renderContent = () => {
    if (view === 'voice') {
      if (subView === 'voice_session' && sessionActive) {
        return <VoiceSession config={wizardConfig} onStop={handleStopSession} />;
      }
      return <VoiceWizard onStartSession={handleStartSession} />;
    }

    switch (view) {
      case 'dashboard': return <DashboardSection />;
      case 'gpu': return <GpuSection />;
      case 'model': return <ModelSection />;
      case 'training': return <TrainingSection />;
      case 'marketplace': return <MarketplaceSection />;
      case 'admin': return <AdminSection />;
      case 'settings': return <SettingsSection />;
      case 'cluster': return <ClusterSection />;
      case 'data': return <DataSection />;
      case 'compute': return <ComputeSection />;
      default: return <DashboardSection />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex">
      {/* Sidebar */}
      <aside className="w-64 min-h-screen bg-slate-900/50 border-r border-slate-800/50 flex flex-col">
        <div className="p-6 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white tracking-tight">Koolaber</h1>
              <p className="text-xs text-slate-500">Sovereign Playground</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
          {NAV_ITEMS.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive(item.id)
                    ? 'bg-purple-600/20 text-purple-400 border border-purple-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.subItems && (
                  voiceExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Voice sub-items */}
              {item.subItems && voiceExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  {item.subItems.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => handleVoiceSubClick(sub.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                        view === 'voice' && subView === sub.id
                          ? 'bg-amber-600/20 text-amber-400 border border-amber-600/30'
                          : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/30 border border-transparent'
                      }`}
                    >
                      <Mic className="w-3.5 h-3.5" />
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800/50">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-slate-400">API Ready</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {renderContent()}
      </main>
    </div>
  );
}

export default App;
